import React from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import DarkModeToggle from 'front/components/ui/DarkModeToggle';
import { useTheme } from '../../../core/contexts/ThemeContext';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background:
    url('/images/global/member_bg_bottom1.png') no-repeat left bottom,
    url('/images/global/member_bg_bottom2.png') no-repeat right bottom,
    url('/images/global/member_bg.png') no-repeat center center;
  background-size: auto, auto, cover;

  html.dark-mode & {
    background:
      url('/images/global/member_bg_bottom1_dark.png') no-repeat left bottom,
      url('/images/global/member_bg_bottom2_dark.png') no-repeat right bottom,
      url('/images/global/member_bg_dark.png') no-repeat center center;
  }
`;


const Header = styled.header`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.xl};
  & button {
    pointer-events: auto;
  }
`;

const Logo = styled.div`
  cursor: pointer;
  pointer-events: auto;
  
  html.dark-mode & img {
    filter: invert(1);
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 320px;
  gap: ${props => props.theme.spacing.xl};
  pointer-events: auto;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSize['2xl']};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.book.color1b};
  text-align: center;
`;

const IdFindResult = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  width: 100%;
  max-width: 320px;
  margin-top: ${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing['2xl']};
  border-radius: ${props => props.theme.borderRadius.lg};
  background-color: ${props => props.theme.colors.white};
`;

const IdMessage = styled.div`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.normal};
  color: ${props => props.theme.colors.book.color6b};
  text-align: center;
`;

const IdList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  width: 100%;
`;

const IdItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.sm} 0;
  font-size: ${props => props.theme.fontSize.lg};
  color: ${props => props.theme.colors.book.color4b};
  text-align: left;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  width: 100%;
  max-width: 320px;
  margin-top: ${props => props.theme.spacing.lg};
`;

const Button = styled.button<{ $disabled: boolean }>`
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: ${props => props.theme.fontWeight.semibold};
  border-radius: ${props => props.theme.borderRadius.lg};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  width: 100%;
  padding: ${props => `${props.theme.spacing.md} ${props.theme.spacing.lg}`};
  height: 44px;
  font-size: ${props => props.theme.fontSize.base};
  background-color: ${props => props.$disabled ? props.theme.colors.book.color4b : props.theme.colors.book.color0b};
  color: ${props => props.theme.colors.white};
  &.white {
    background-color: ${props => props.theme.colors.white};
    border: 1px solid ${props => props.theme.colors.border.ce};
    color: ${props => props.theme.colors.book.color3b};
  }
`;

const FindIdResult: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const loginIds = (location.state as any)?.loginIds || [];

  return (
    <Container>
      <Header>
        <Logo onClick={() => navigate('/')}><img src="/images/global/logo.svg" alt="logo" /></Logo>
        <DarkModeToggle isDark={isDarkMode} onToggle={toggleDarkMode} />
      </Header>
      <Content>
        <Title>아이디 찾기</Title>
        <IdFindResult>
        {loginIds.length > 0 ? (
          <>
            <IdMessage>회원님의 아이디는 다음과 같습니다.</IdMessage>
            <IdList>
              {loginIds.map((id: string, index: number) => (
                <IdItem key={index}>
                  <span>{id}</span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(id);
                      alert('아이디가 복사되었습니다.');
                    }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 'inherit' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-label="복사">
                      <path d="M20.998 10C20.986 7.825 20.89 6.647 20.121 5.879C19.243 5 17.828 5 15 5H12C9.172 5 7.757 5 6.879 5.879C6 6.757 6 8.172 6 11V16C6 18.828 6 20.243 6.879 21.121C7.757 22 9.172 22 12 22H15C17.828 22 19.243 22 20.121 21.121C21 20.243 21 18.828 21 16V15" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M3 10V16C3 16.7956 3.31607 17.5587 3.87868 18.1213C4.44129 18.6839 5.20435 19 6 19M18 5C18 4.20435 17.6839 3.44129 17.1213 2.87868C16.5587 2.31607 15.7956 2 15 2H11C7.229 2 5.343 2 4.172 3.172C3.518 3.825 3.229 4.7 3.102 6" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </button>
                </IdItem>
              ))}
            </IdList>
          </>
        ) : (
          <IdMessage>등록된 ID를 찾을 수 없습니다.</IdMessage>
        )}
        </IdFindResult>
        <ButtonGroup>
          <Button onClick={() => navigate('/login')} $disabled={false}>
            로그인 하기
          </Button>
          <Button onClick={() => navigate('/reset-password')} $disabled={false} className='white'>
            비밀번호 재설정
          </Button>
        </ButtonGroup>
      </Content>
    </Container>
  );
};

export default FindIdResult;
